import { FC, useState, useRef, useMemo, useEffect, useCallback, lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';

import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { successToast, errorToast } from '@/helpers';
import { ApiService } from '@/support/ApiService.ts';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { _notNil, _isEmptyString, _isNil } from '@/littledash';
import { featuresSelector } from '@/support/Selectors';
import Loading from '@/components/Loading';

import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import type { components } from '@/generated/internal-api/openapi-types';
import {
  validateObservationTypeTitle,
  validateObservationTypeLabel,
  parseScoringScale,
  formatScoringScale,
  DEFAULT_NUMERIC_SCALE_MIN,
  DEFAULT_NUMERIC_SCALE_MAX,
  type ScoringScale,
  type ValidationData,
  type ObservationTypeRow,
} from '@/utils/observationTypeValidation';

const BulkEditObservationTypesTableV12 = lazy(() => import('./BulkEditObservationTypesTableV12'));
const BulkEditObservationTypesTableV16 = lazy(() => import('./BulkEditObservationTypesTableV16'));

type ObservationUpdatePayloadItem = components['schemas']['TeamObservationGlossaryUpdateItemV1.schema'];

const hotUpgradeSelector = createSelector([featuresSelector], (features) => ({
  hotUpgrade: features?.hot_upgrade ?? false,
}));

interface BulkEditObservationTypesProps {
  observationTypes: Array<TeamGlossaryV1>;
  onSave: () => void;
  onCancel: () => void;
}

const BulkEditObservationTypes: FC<BulkEditObservationTypesProps> = ({ observationTypes, onSave, onCancel }) => {
  const { hotUpgrade } = useSelector(hotUpgradeSelector);
  const [saving, setSaving] = useState(false);
  const [validationData, setValidationData] = useState<ValidationData | null>(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const hasEditableRows = useMemo(
    () => observationTypes.some((ot) => ot.system !== true && _isNil(ot.archived_at)),
    [observationTypes]
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hotTableRef = useRef<any>(null);

  useEffect(() => {
    const fetchValidationData = async (): Promise<void> => {
      try {
        const response = await ApiService.call({
          endpoint: 'GET /api/v1/team-glossary/observation-validation-data',
          path: undefined,
          options: {
            onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-observation-types-validation-load' },
          },
        });
        setValidationData(response.body as ValidationData);
      } catch (error) {
        errorToast('Failed to load validation data');
        ExceptionHandler.captureException(
          new InVivoError('Failed to load validation data', {
            cause: error,
            slug: 'bulk-edit-observation-types-validation-load',
          })
        );
        setValidationData(null);
      } finally {
        setLoadingValidation(false);
      }
    };

    void fetchValidationData();
  }, []);

  const validateTitle = useCallback(
    (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
      const hotInstance = hotTableRef.current?.hotInstance;
      const spreadsheetData = _notNil(hotInstance) ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];
      return validateObservationTypeTitle(value, rowIndex, validationData, observationTypes, spreadsheetData);
    },
    [validationData, observationTypes]
  );

  const validateLabel = useCallback(
    (value: string, rowIndex: number): { isValid: boolean; message?: string } => {
      const hotInstance = hotTableRef.current?.hotInstance;
      const spreadsheetData = _notNil(hotInstance) ? (hotInstance.getData() as Array<ObservationTypeRow>) : [];
      return validateObservationTypeLabel(value, rowIndex, validationData, observationTypes, spreadsheetData);
    },
    [validationData, observationTypes]
  );

  const initialData: Array<ObservationTypeRow> = useMemo(() => {
    return observationTypes.map((observationType) => {
      const gradingInfo = observationType.description || '';
      let scoringScale = '';

      if (observationType.type === 'numeric' && _notNil(observationType.options)) {
        const scale: ScoringScale = {
          type: 'numeric',
          minValue: Number(observationType.options.min) || DEFAULT_NUMERIC_SCALE_MIN,
          maxValue: Number(observationType.options.max) || DEFAULT_NUMERIC_SCALE_MAX,
        };
        scoringScale = formatScoringScale(scale);
      } else if (observationType.type === 'severity') {
        scoringScale = 'Severity';
      }

      return [observationType.title ?? '', observationType.label || '', scoringScale, gradingInfo];
    });
  }, [observationTypes]);

  const handleSave = async (): Promise<void> => {
    const hotInstance = hotTableRef.current?.hotInstance;
    if (_isNil(hotInstance)) {
      errorToast('Unable to access table data');
      return;
    }

    setSaving(true);

    try {
      const currentData = (hotInstance.getData?.() ?? []) as Array<ObservationTypeRow>;

      const teamGlossariesPayload: Array<ObservationUpdatePayloadItem> = [];

      currentData.forEach((row, index) => {
        const [title, label, scoringScale, gradingInfo] = row;
        const observationType = observationTypes[index];

        const isSystem = observationType.system === true;
        const isArchived = _notNil(observationType.archived_at);
        if (isSystem || isArchived) {
          return;
        }

        const cleanTitle = (title ?? '').toString().trim();
        const cleanLabel = (label ?? '').toString().trim();
        const cleanGradingInfo = (gradingInfo ?? '').toString().trim();

        const scale = parseScoringScale(scoringScale as string);
        let type: 'none' | 'numeric' | 'severity' = 'none';
        let options: { min: string; max: string } | null = null;

        if (_notNil(scale)) {
          if (scale.type === 'numeric') {
            type = 'numeric';
            options = {
              min: scale.minValue.toString(),
              max: scale.maxValue.toString(),
            };
          } else if (scale.type === 'severity') {
            type = 'severity';
          }
        }

        const hasLabel = !_isEmptyString(cleanLabel);
        const hasDescription = !_isEmptyString(cleanGradingInfo);

        teamGlossariesPayload.push({
          team_glossary_api_id: observationType.api_id,
          title: cleanTitle,
          type,
          label: hasLabel ? cleanLabel : null,
          description: hasDescription ? cleanGradingInfo : null,
          ...(_notNil(options) && { options }),
        });
      });

      await ApiService.call({
        endpoint: 'PATCH /api/v1/team-glossary/observations/bulk-update',
        path: undefined,
        body: { team_glossaries: teamGlossariesPayload },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-edit-observation-types-save' } },
      });

      successToast('Observation types updated successfully');
      onSave();
    } catch (error) {
      errorToast('Failed to update observation types');
      ExceptionHandler.captureException(
        new InVivoError('Failed to update observation types', {
          cause: error,
          slug: 'bulk-edit-observation-types-save',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalContainer className="bg-white" data-testid="bulk-edit-observation-types-modal">
      <ModalHeader
        title="Edit observation types"
        closeModal={onCancel}
        closeDisabled={saving}
        className="pa3 bb b--moon-gray w-100"
      />
      <div className="pa3">
        {loadingValidation ? (
          <div className="tc pa4" data-testid="loading-validation-data">
            <div className="mb2">Loading validation data...</div>
          </div>
        ) : (
          <Suspense fallback={<Loading />}>
            {hotUpgrade ? (
              <BulkEditObservationTypesTableV16
                data={initialData}
                innerRef={hotTableRef}
                observationTypes={observationTypes}
                validationData={validationData}
                setInvalid={setHasValidationErrors}
              />
            ) : (
              <BulkEditObservationTypesTableV12
                data={initialData}
                innerRef={hotTableRef}
                observationTypes={observationTypes}
                validationData={validationData}
                setInvalid={setHasValidationErrors}
                validateTitle={validateTitle}
                validateLabel={validateLabel}
              />
            )}
          </Suspense>
        )}
      </div>

      <ModalActions
        className="pa3 bt b--moon-gray w-100"
        onSubmit={handleSave}
        onCancel={onCancel}
        submitBtnText="Update"
        cancelBtnText="Close"
        submitButtonProps={{
          loading: saving,
          disabled: saving || loadingValidation || hasValidationErrors || !hasEditableRows,
          testId: 'edit-observation-types-submit',
        }}
        cancelButtonProps={{
          disabled: saving,
          testId: 'edit-observation-types-cancel',
        }}
      />
    </ModalContainer>
  );
};

export default BulkEditObservationTypes;
